import { copierQuantitiesFromSubscriptions, hasCopierSeats, listSeatEntitledSubscriptions, subscriptionIncludesProduct } from 'lib/billing';
import {
  ADDONS,
  ADDON_PRODUCTS,
  allEnvsAreFutures,
  dedicatedEnvMonthlyEquivCents,
  isFuturesDedicatedKind,
  sumDedicatedEnvCents
} from 'lib/billing/config';
import {
  analyzerOwnerForCycle,
  computeLineAmountCents,
  computePricing,
  extractDedicatedArrays,
  findActiveAnalyzer,
  productFromSubscription,
  resolveCatalogPrice,
  subscriptionBillingCycle,
  summarizeSubscriptions
} from './libBilling';
import type { BillingState, Selection } from './types';

export interface PricingBundle {
  pricing: ReturnType<typeof computePricing>;
  addonsPricing: {
    analyzerCycleCents: number;
    dedicatedCycleCents: number;
    mcpCycleCents: number;
    analyzerWouldBeCycleCents: number;
    dedicatedWouldBeCycleCents: number;
    addonsTotalCycleCents: number;
    addonsWouldBeCycleCents: number;
  };
  totals: {
    grandTotalCycleCents: number;
    grandTotalWouldBeCycleCents: number;
    grandTotalSavingsCents: number;
    grandTotalSavingsPercent: number;
  };
  flags: {
    hasCopierAccounts: boolean;
    analyzerSubscribed: boolean;
    analyzerOn: boolean;
    analyzerPurchasing: boolean;
    dedicatedActive: boolean;
    dedicatedEnvCount: number;
    dedicatedAccountsTotal: number;
    dedicatedAllFutures: boolean;
  };
}

export interface AnalyzerOwnership {
  owned: boolean;
  ownedCycle: string | null;
  ownedElsewhere: boolean;
  ownershipPeriodEnd: number | null;
}

export function selectAnalyzerOwnership(
  subscriptions: BillingState['subscriptions'],
  catalog: BillingState['catalog'],
  billingCycle: string
): AnalyzerOwnership {
  const owner = findActiveAnalyzer(subscriptions, catalog);
  if (!owner) {
    return {
      owned: false,
      ownedCycle: null,
      ownedElsewhere: false,
      ownershipPeriodEnd: null
    };
  }
  return {
    owned: true,
    ownedCycle: owner.cycle,
    ownedElsewhere: owner.cycle !== billingCycle,
    ownershipPeriodEnd: owner.currentPeriodEnd
  };
}

export function derivePricingBundle(input: {
  selection: Selection;
  catalog: BillingState['catalog'];
  subscriptions: BillingState['subscriptions'];
  trialUsedProductIds?: string[];
}): PricingBundle {
  const { selection, catalog, subscriptions, trialUsedProductIds } = input;

  const pricing = computePricing(selection, catalog, trialUsedProductIds || []);
  const hasCopierAccounts = hasCopierSeats(selection);

  const cycleOwner = analyzerOwnerForCycle(subscriptions, selection.billingCycle, catalog);
  const analyzerSubscribed = Boolean(cycleOwner?.subscription);
  const analyzerSelected = Boolean(selection.analyzerEnabled);
  const analyzerOn = analyzerSubscribed || (hasCopierAccounts && analyzerSelected);
  const analyzerPurchasing = hasCopierAccounts && analyzerSelected && !analyzerSubscribed;

  const dedicated = extractDedicatedArrays(selection);
  const dedicatedEnvCount = dedicated.envs.length;
  // Dedicated Env is purchasable on its own — it does not require copier
  // accounts (unlike Analyzer, which stays gated on `hasCopierAccounts`).
  const dedicatedActive = dedicatedEnvCount > 0;
  // A futures env is unlimited and carries a placeholder count of 1, so it
  // contributes no counted accounts; an all-futures cart says "unlimited" instead.
  const dedicatedAccountsTotal = dedicated.envs.reduce(
    (sum: number, a: number, i: number) => sum + (isFuturesDedicatedKind(dedicated.kinds[i]) ? 0 : Math.max(1, a || 1)),
    0
  );
  const dedicatedAllFutures = allEnvsAreFutures(dedicated.envs, dedicated.kinds);
  // Kind-aware monthly-equivalent list price — only for the "would-be" total.
  const dedicatedMonthlyEquivCents = dedicatedActive
    ? dedicated.envs.reduce((sum: number, accts: number, i: number) => sum + dedicatedEnvMonthlyEquivCents(dedicated.kinds[i], accts), 0)
    : 0;

  const analyzerPurchasingPrice =
    analyzerPurchasing && catalog
      ? resolveCatalogPrice(catalog, ADDON_PRODUCTS.analyzer, selection.billingCycle, {
          subscriptions
        })
      : null;

  const paidMonths = pricing.paidMonths || 1;
  const multiplier = pricing.multiplier || 1;

  const analyzerCycleCents = analyzerPurchasing
    ? analyzerPurchasingPrice
      ? computeLineAmountCents(analyzerPurchasingPrice, 1)
      : ADDONS.analyzer.priceCentsMonthly * paidMonths
    : 0;
  // Kind-aware plan total (owned envs included; not amount-due-now).
  const dedicatedCycleCents = dedicatedActive ? sumDedicatedEnvCents(dedicated, selection.billingCycle, paidMonths) : 0;
  const analyzerWouldBeCycleCents = analyzerPurchasing ? ADDONS.analyzer.priceCentsMonthly * multiplier : 0;
  const dedicatedWouldBeCycleCents = dedicatedActive ? dedicatedMonthlyEquivCents * multiplier : 0;

  // MCP is a flat standalone add-on (like Dedicated Env, NOT copier-gated). Its
  // cost is the resolved catalog price for the cycle, or the contract fallback.
  const mcpSelected = Boolean(selection.mcpEnabled);
  const mcpPrice =
    mcpSelected && catalog ? resolveCatalogPrice(catalog, ADDON_PRODUCTS.mcp, selection.billingCycle, { subscriptions }) : null;
  const mcpCycleCents = mcpSelected
    ? mcpPrice
      ? computeLineAmountCents(mcpPrice, 1)
      : (ADDONS.mcp.priceCentsByCycle as Record<string, number>)[selection.billingCycle] ?? ADDONS.mcp.priceCentsMonthly
    : 0;

  const addonsPricing = {
    analyzerCycleCents,
    dedicatedCycleCents,
    mcpCycleCents,
    analyzerWouldBeCycleCents,
    dedicatedWouldBeCycleCents,
    addonsTotalCycleCents: analyzerCycleCents + dedicatedCycleCents + mcpCycleCents,
    addonsWouldBeCycleCents: analyzerWouldBeCycleCents + dedicatedWouldBeCycleCents + mcpCycleCents
  };

  const tierWouldBeCycleCents = (pricing.monthlyCents || 0) * multiplier;
  const grandTotalCycleCents = (pricing.cycleCents || 0) + addonsPricing.addonsTotalCycleCents;
  const grandTotalWouldBeCycleCents = tierWouldBeCycleCents + addonsPricing.addonsWouldBeCycleCents;
  const grandTotalSavingsCents = Math.max(0, grandTotalWouldBeCycleCents - grandTotalCycleCents);
  const grandTotalSavingsPercent =
    grandTotalWouldBeCycleCents > 0 ? Math.round((grandTotalSavingsCents / grandTotalWouldBeCycleCents) * 100) : 0;

  return {
    pricing,
    addonsPricing,
    totals: {
      grandTotalCycleCents,
      grandTotalWouldBeCycleCents,
      grandTotalSavingsCents,
      grandTotalSavingsPercent
    },
    flags: {
      hasCopierAccounts,
      analyzerSubscribed,
      analyzerOn,
      analyzerPurchasing,
      dedicatedActive,
      dedicatedEnvCount,
      dedicatedAccountsTotal,
      dedicatedAllFutures
    }
  };
}

// Per-product copier seat counts from seat-entitled (active/trialing) subs only.
// Line-item aware; past_due subs grant no seats. Shared by the seat selectors.
function seatCountsOf(subscriptions: BillingState['subscriptions']) {
  return copierQuantitiesFromSubscriptions(listSeatEntitledSubscriptions(subscriptions as any[]), null);
}

/**
 * Copier seat limit (Standard + Premium + Futures). Excludes add-on lines
 * (Analyzer, Dedicated Env). Use everywhere a seat limit shows so surfaces agree.
 */
export function selectCopierAccountLimit(subscriptions: BillingState['subscriptions']): number {
  const counts = seatCountsOf(subscriptions);
  return (counts.standard || 0) + (counts.premium || 0) + (counts.futures || 0);
}

/** Premium copier seats only (line-item aware). */
export function selectPremiumSeatLimit(subscriptions: BillingState['subscriptions']): number {
  return seatCountsOf(subscriptions).premium || 0;
}

/** Whether a seat-entitled sub includes the Analyzer add-on (line-item aware). */
export function selectHasAnalyzerSeat(subscriptions: BillingState['subscriptions']): boolean {
  return subscriptionIncludesProduct(listSeatEntitledSubscriptions(subscriptions as any[]), ADDON_PRODUCTS.analyzer, null);
}

export function selectActiveProductLines(subscriptions: BillingState['subscriptions'], billingCycle: string): Set<string> {
  const lines = new Set<string>();
  for (const sub of summarizeSubscriptions(subscriptions).activeSubs) {
    if (subscriptionBillingCycle(sub) !== billingCycle) continue;
    const product = productFromSubscription(sub);
    if (product) lines.add(product);
  }
  return lines;
}
